import { useState, useEffect } from 'react'
import { Shield, Image, Award, Trash2, User, AlertCircle, Loader2, Search, Users } from 'lucide-react'
import { userAPI, badgeAPI, authAPI, adminAPI, tournamentsAPI, teamImagesAPI } from '@/lib/api'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs'

export default function ModerationPanel() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  // Users with avatars
  const [users, setUsers] = useState([])
  const [usersLoading, setUsersLoading] = useState(false)
  const [userSearchQuery, setUserSearchQuery] = useState('')

  // Tournament participants with images
  const [tournaments, setTournaments] = useState([])
  const [tournamentsLoading, setTournamentsLoading] = useState(false)
  const [selectedTournament, setSelectedTournament] = useState(null)
  const [participants, setParticipants] = useState([])
  const [participantsLoading, setParticipantsLoading] = useState(false)

  // Badge management
  const [badgeTypes, setBadgeTypes] = useState([])
  const [badgeTypesLoading, setBadgeTypesLoading] = useState(false)
  const [userBadges, setUserBadges] = useState([])
  const [selectedUserForBadge, setSelectedUserForBadge] = useState(null)
  const [badgeDialogOpen, setBadgeDialogOpen] = useState(false)
  const [badgeSearchQuery, setBadgeSearchQuery] = useState('')
  const [badgeSearchResults, setBadgeSearchResults] = useState([])
  const [badgeSearching, setBadgeSearching] = useState(false)

  // New badge type dialog
  const [createBadgeDialogOpen, setCreateBadgeDialogOpen] = useState(false)
  const [newBadgeForm, setNewBadgeForm] = useState({
    name: '',
    description: '',
    icon_url: '',
    color: '#3b82f6'
  })

  // Fetch users with avatars
  const fetchUsersWithAvatars = async () => {
    try {
      setUsersLoading(true)
      setError(null)
      const response = await adminAPI.getUsers()
      // Filter users with avatars
      const usersWithAvatars = response.data.users.filter(u => u.avatar_url)
      setUsers(usersWithAvatars)
    } catch (err) {
      console.error('Failed to fetch users:', err)
      setError(err.response?.data?.error || 'Failed to load users')
    } finally {
      setUsersLoading(false)
    }
  }

  // Fetch tournaments
  const fetchTournaments = async () => {
    try {
      setTournamentsLoading(true)
      setError(null)
      const response = await tournamentsAPI.getTournaments()
      setTournaments(response.data.tournaments || [])
    } catch (err) {
      console.error('Failed to fetch tournaments:', err)
      setError(err.response?.data?.error || 'Failed to load tournaments')
    } finally {
      setTournamentsLoading(false)
    }
  }

  // Fetch participants for selected tournament
  const fetchParticipants = async (tournamentId) => {
    try {
      setParticipantsLoading(true)
      setError(null)
      const response = await tournamentsAPI.getTournamentById(tournamentId)
      const participantsWithImages = response.data.participants.filter(
        p => p.team_logo_url || p.team_banner_url
      )
      setParticipants(participantsWithImages)
    } catch (err) {
      console.error('Failed to fetch participants:', err)
      setError(err.response?.data?.error || 'Failed to load participants')
    } finally {
      setParticipantsLoading(false)
    }
  }

  // Fetch badge types
  const fetchBadgeTypes = async () => {
    try {
      setBadgeTypesLoading(true)
      setError(null)
      const response = await badgeAPI.getAllTypes()
      setBadgeTypes(response.data.badgeTypes || [])
    } catch (err) {
      console.error('Failed to fetch badge types:', err)
      setError(err.response?.data?.error || 'Failed to load badge types')
    } finally {
      setBadgeTypesLoading(false)
    }
  }

  useEffect(() => {
    fetchUsersWithAvatars()
    fetchTournaments()
    fetchBadgeTypes()
  }, [])

  useEffect(() => {
    if (selectedTournament) {
      fetchParticipants(selectedTournament)
    }
  }, [selectedTournament])

  // Search users for badge assignment
  useEffect(() => {
    const searchUsers = async () => {
      if (!badgeSearchQuery || badgeSearchQuery.length < 2) {
        setBadgeSearchResults([])
        return
      }

      try {
        setBadgeSearching(true)
        const response = await authAPI.searchUsers(badgeSearchQuery)
        setBadgeSearchResults(response.data.users || [])
      } catch (err) {
        console.error('User search failed:', err)
        setBadgeSearchResults([])
      } finally {
        setBadgeSearching(false)
      }
    }

    const debounceTimer = setTimeout(searchUsers, 300)
    return () => clearTimeout(debounceTimer)
  }, [badgeSearchQuery])

  // Handle remove avatar
  const handleRemoveAvatar = async (userId) => {
    if (!confirm('Are you sure you want to remove this user\'s avatar?')) return

    try {
      setLoading(true)
      setError(null)
      await userAPI.adminRemoveAvatar(userId)
      setSuccess('Avatar removed successfully')
      fetchUsersWithAvatars()
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Failed to remove avatar:', err)
      setError(err.response?.data?.error || 'Failed to remove avatar')
    } finally {
      setLoading(false)
    }
  }

  // Handle remove team logo
  const handleRemoveTeamLogo = async (participantId) => {
    if (!confirm('Are you sure you want to remove this team\'s logo?')) return

    try {
      setLoading(true)
      setError(null)
      await teamImagesAPI.adminRemoveLogo(participantId)
      setSuccess('Team logo removed successfully')
      if (selectedTournament) {
        fetchParticipants(selectedTournament)
      }
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Failed to remove team logo:', err)
      setError(err.response?.data?.error || 'Failed to remove team logo')
    } finally {
      setLoading(false)
    }
  }

  // Handle remove team banner
  const handleRemoveTeamBanner = async (participantId) => {
    if (!confirm('Are you sure you want to remove this team\'s banner?')) return

    try {
      setLoading(true)
      setError(null)
      await teamImagesAPI.adminRemoveBanner(participantId)
      setSuccess('Team banner removed successfully')
      if (selectedTournament) {
        fetchParticipants(selectedTournament)
      }
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Failed to remove team banner:', err)
      setError(err.response?.data?.error || 'Failed to remove team banner')
    } finally {
      setLoading(false)
    }
  }

  // Handle create badge type
  const handleCreateBadgeType = async (e) => {
    e.preventDefault()

    try {
      setLoading(true)
      setError(null)
      await badgeAPI.createType(newBadgeForm)
      setSuccess('Badge type created successfully')
      setCreateBadgeDialogOpen(false)
      setNewBadgeForm({ name: '', description: '', icon_url: '', color: '#3b82f6' })
      fetchBadgeTypes()
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Failed to create badge type:', err)
      setError(err.response?.data?.error || 'Failed to create badge type')
    } finally {
      setLoading(false)
    }
  }

  // Handle delete badge type
  const handleDeleteBadgeType = async (badgeTypeId) => {
    if (!confirm('Are you sure you want to delete this badge type? This will remove it from all users.')) return

    try {
      setLoading(true)
      setError(null)
      await badgeAPI.deleteBadgeType(badgeTypeId)
      setSuccess('Badge type deleted successfully')
      fetchBadgeTypes()
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Failed to delete badge type:', err)
      setError(err.response?.data?.error || 'Failed to delete badge type')
    } finally {
      setLoading(false)
    }
  }

  // Handle open badge management for user
  const handleManageUserBadges = async (userId) => {
    try {
      setSelectedUserForBadge(userId)
      setBadgeDialogOpen(true)
      setLoading(true)
      const response = await badgeAPI.getUserBadges(userId)
      setUserBadges(response.data.badges || [])
    } catch (err) {
      console.error('Failed to fetch user badges:', err)
      setError(err.response?.data?.error || 'Failed to load user badges')
    } finally {
      setLoading(false)
    }
  }

  // Handle award badge
  const handleAwardBadge = async (badgeTypeId) => {
    try {
      setLoading(true)
      setError(null)
      await badgeAPI.awardBadge(selectedUserForBadge, badgeTypeId)
      setSuccess('Badge awarded successfully')
      const response = await badgeAPI.getUserBadges(selectedUserForBadge)
      setUserBadges(response.data.badges || [])
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Failed to award badge:', err)
      setError(err.response?.data?.error || 'Failed to award badge')
    } finally {
      setLoading(false)
    }
  }

  // Handle revoke badge
  const handleRevokeBadge = async (badgeTypeId) => {
    try {
      setLoading(true)
      setError(null)
      await badgeAPI.revokeBadge(selectedUserForBadge, badgeTypeId)
      setSuccess('Badge revoked successfully')
      const response = await badgeAPI.getUserBadges(selectedUserForBadge)
      setUserBadges(response.data.badges || [])
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Failed to revoke badge:', err)
      setError(err.response?.data?.error || 'Failed to revoke badge')
    } finally {
      setLoading(false)
    }
  }

  const getUserInitials = (username, displayName) => {
    const name = displayName || username
    return name ? name.substring(0, 2).toUpperCase() : '?'
  }

  const filteredUsers = userSearchQuery
    ? users.filter(u =>
        u.username?.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
        u.display_name?.toLowerCase().includes(userSearchQuery.toLowerCase())
      )
    : users

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold glow-text-pink flex items-center gap-2">
          <Shield className="h-6 w-6" />
          Moderation Panel
        </h2>
        <p className="text-muted-foreground mt-1">
          Manage user images, team assets, and achievement badges
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="border-green-500/30 bg-green-500/10">
          <AlertDescription className="text-green-400">{success}</AlertDescription>
        </Alert>
      )}

      {/* Tabs for different moderation categories */}
      <Tabs defaultValue="avatars" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3 bg-muted/50">
          <TabsTrigger value="avatars">User Avatars</TabsTrigger>
          <TabsTrigger value="teams">Team Images</TabsTrigger>
          <TabsTrigger value="badges">Badges</TabsTrigger>
        </TabsList>

        {/* User Avatars Tab */}
        <TabsContent value="avatars" className="space-y-4">
          <Card className="border-neon-pink/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Image className="h-5 w-5" />
                User Avatars ({filteredUsers.length})
              </CardTitle>
              <CardDescription>
                Manage and moderate user profile pictures
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Search */}
              <div className="flex items-center gap-2">
                <Search className="h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search users by username or display name..."
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                  className="flex-1"
                />
              </div>

              {/* Users Table */}
              {usersLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-neon-pink" />
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No users with avatars found
                </div>
              ) : (
                <div className="border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Avatar</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredUsers.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell>
                            <div>
                              <div className="font-medium">{user.username}</div>
                              {user.display_name && (
                                <div className="text-sm text-muted-foreground">{user.display_name}</div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Avatar className="h-10 w-10 border border-neon-pink/30">
                              {user.avatar_url && <AvatarImage src={user.avatar_url} alt={user.username} />}
                              <AvatarFallback className="bg-neon-pink/10 text-neon-pink">
                                {getUserInitials(user.username, user.display_name)}
                              </AvatarFallback>
                            </Avatar>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={
                              user.role === 'superadmin' ? 'border-yellow-400/70 text-yellow-400 bg-yellow-400/10' :
                              ['admin','superadmin'].includes(user?.role) ? 'border-red-500/50 text-red-500' :
                              user.role === 'referee' ? 'border-yellow-500/50 text-yellow-500' :
                              user.role === 'helpdesk' ? 'border-purple-500/50 text-purple-400' :
                              'border-blue-500/50 text-blue-400'
                            }>{user.role}</Badge>
                          </TableCell>
                          <TableCell className="text-right space-x-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleManageUserBadges(user.id)}
                              className="border-blue-500/50 hover:bg-blue-500/20"
                            >
                              <Award className="h-4 w-4 mr-1" />
                              Badges
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleRemoveAvatar(user.id)}
                              disabled={loading}
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Remove
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Team Images Tab */}
        <TabsContent value="teams" className="space-y-4">
          <Card className="border-neon-pink/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Team Logos & Banners
              </CardTitle>
              <CardDescription>
                Manage tournament team images
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Tournament Selection */}
              <div>
                <label className="text-sm font-medium mb-2 block">Select Tournament</label>
                <Select value={selectedTournament || ''} onValueChange={setSelectedTournament}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a tournament" />
                  </SelectTrigger>
                  <SelectContent>
                    {tournaments.map((tournament) => (
                      <SelectItem key={tournament.id} value={tournament.id.toString()}>
                        {tournament.name} ({tournament.game})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Participants Table */}
              {selectedTournament && (
                participantsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-neon-pink" />
                  </div>
                ) : participants.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No teams with images in this tournament
                  </div>
                ) : (
                  <div className="border rounded-md">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Team Name</TableHead>
                          <TableHead>Logo</TableHead>
                          <TableHead>Banner</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {participants.map((participant) => (
                          <TableRow key={participant.id}>
                            <TableCell className="font-medium">{participant.team_name}</TableCell>
                            <TableCell>
                              {participant.team_logo_url ? (
                                <img
                                  src={participant.team_logo_url}
                                  alt="Team logo"
                                  className="h-10 w-10 rounded object-cover border border-neon-blue/30"
                                />
                              ) : (
                                <span className="text-sm text-muted-foreground">No logo</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {participant.team_banner_url ? (
                                <img
                                  src={participant.team_banner_url}
                                  alt="Team banner"
                                  className="h-10 w-16 rounded object-cover border border-neon-blue/30"
                                />
                              ) : (
                                <span className="text-sm text-muted-foreground">No banner</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right space-x-2">
                              {participant.team_logo_url && (
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => handleRemoveTeamLogo(participant.id)}
                                  disabled={loading}
                                >
                                  <Trash2 className="h-4 w-4 mr-1" />
                                  Remove Logo
                                </Button>
                              )}
                              {participant.team_banner_url && (
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => handleRemoveTeamBanner(participant.id)}
                                  disabled={loading}
                                >
                                  <Trash2 className="h-4 w-4 mr-1" />
                                  Remove Banner
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Badges Tab */}
        <TabsContent value="badges" className="space-y-4">
          <Card className="border-neon-pink/30">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Award className="h-5 w-5" />
                    Badge Types ({badgeTypes.length})
                  </CardTitle>
                  <CardDescription>
                    Manage achievement badge types
                  </CardDescription>
                </div>
                <Button
                  onClick={() => setCreateBadgeDialogOpen(true)}
                  className="bg-neon-pink/10 hover:bg-neon-pink/20 text-neon-pink border border-neon-pink/30"
                >
                  Create Badge Type
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {badgeTypesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-neon-pink" />
                </div>
              ) : badgeTypes.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No badge types created yet
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {badgeTypes.map((badge) => (
                    <Card key={badge.id} className="border-neon-blue/30">
                      <CardContent className="p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-2xl">{badge.icon_url}</span>
                            <div>
                              <div className="font-medium">{badge.name}</div>
                              <div className="text-xs text-muted-foreground">{badge.description}</div>
                            </div>
                          </div>
                          <div
                            className="h-4 w-4 rounded-full"
                            style={{ backgroundColor: badge.color }}
                          />
                        </div>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteBadgeType(badge.id)}
                          disabled={loading}
                          className="w-full"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Badge Management Dialog */}
      <Dialog open={badgeDialogOpen} onOpenChange={setBadgeDialogOpen}>
        <DialogContent className="sm:max-w-[500px] border-neon-pink/30">
          <DialogHeader>
            <DialogTitle className="text-neon-pink">Manage User Badges</DialogTitle>
            <DialogDescription>
              Award or revoke achievement badges for this user
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Current badges */}
            <div>
              <h4 className="font-medium mb-2">Current Badges</h4>
              {userBadges.length === 0 ? (
                <div className="text-sm text-muted-foreground">No badges awarded yet</div>
              ) : (
                <div className="space-y-2">
                  {userBadges.map((badge) => (
                    <div
                      key={badge.id}
                      className="flex items-center justify-between p-2 rounded border border-border bg-muted/30"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{badge.icon_url}</span>
                        <div>
                          <div className="font-medium text-sm">{badge.name}</div>
                          <div className="text-xs text-muted-foreground">{badge.description}</div>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleRevokeBadge(badge.badge_type_id)}
                        disabled={loading}
                      >
                        Revoke
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Award new badge */}
            <div>
              <h4 className="font-medium mb-2">Award New Badge</h4>
              <div className="space-y-2">
                {badgeTypes
                  .filter(bt => !userBadges.find(ub => ub.badge_type_id === bt.id))
                  .map((badgeType) => (
                    <div
                      key={badgeType.id}
                      className="flex items-center justify-between p-2 rounded border border-border hover:bg-accent transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{badgeType.icon_url}</span>
                        <div>
                          <div className="font-medium text-sm">{badgeType.name}</div>
                          <div className="text-xs text-muted-foreground">{badgeType.description}</div>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleAwardBadge(badgeType.id)}
                        disabled={loading}
                        className="bg-neon-green/10 hover:bg-neon-green/20 text-neon-green border border-neon-green/30"
                      >
                        Award
                      </Button>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Badge Type Dialog */}
      <Dialog open={createBadgeDialogOpen} onOpenChange={setCreateBadgeDialogOpen}>
        <DialogContent className="sm:max-w-[425px] border-neon-pink/30">
          <form onSubmit={handleCreateBadgeType}>
            <DialogHeader>
              <DialogTitle className="text-neon-pink">Create Badge Type</DialogTitle>
              <DialogDescription>
                Define a new achievement badge type
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <label htmlFor="name" className="text-sm font-medium">
                  Badge Name *
                </label>
                <Input
                  id="name"
                  value={newBadgeForm.name}
                  onChange={(e) => setNewBadgeForm({ ...newBadgeForm, name: e.target.value })}
                  placeholder="Tournament Winner"
                  required
                />
              </div>
              <div className="grid gap-2">
                <label htmlFor="description" className="text-sm font-medium">
                  Description *
                </label>
                <Input
                  id="description"
                  value={newBadgeForm.description}
                  onChange={(e) => setNewBadgeForm({ ...newBadgeForm, description: e.target.value })}
                  placeholder="Won a tournament"
                  required
                />
              </div>
              <div className="grid gap-2">
                <label htmlFor="icon_url" className="text-sm font-medium">
                  Icon (Emoji) *
                </label>
                <Input
                  id="icon_url"
                  value={newBadgeForm.icon_url}
                  onChange={(e) => setNewBadgeForm({ ...newBadgeForm, icon_url: e.target.value })}
                  placeholder="🏆"
                  required
                />
              </div>
              <div className="grid gap-2">
                <label htmlFor="color" className="text-sm font-medium">
                  Color
                </label>
                <div className="flex items-center gap-2">
                  <Input
                    id="color"
                    type="color"
                    value={newBadgeForm.color}
                    onChange={(e) => setNewBadgeForm({ ...newBadgeForm, color: e.target.value })}
                    className="w-20"
                  />
                  <Input
                    value={newBadgeForm.color}
                    onChange={(e) => setNewBadgeForm({ ...newBadgeForm, color: e.target.value })}
                    placeholder="#3b82f6"
                    className="flex-1"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setCreateBadgeDialogOpen(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-neon-pink/10 hover:bg-neon-pink/20 text-neon-pink border border-neon-pink/30"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Badge Type'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
